import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { CreateLicenseTypeRequest, LicenseType, UpdateLicenseTypeRequest } from '@models/csp';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { addEntity, createCustomStateSetOnUrlHandler, deleteEntity, getAll, StateDictionary, updateEntity } from '../../helpers';
import { LicenseTypesService } from './license-types.service';

type LicenseTypesState = {
  licenseTypes: LicenseType[];
  selectedLicenseTypeId: string | null;
  isLoading: boolean;
  licenseTypeCreateLoading: boolean;
};

const initialState: LicenseTypesState = {
  licenseTypes: [],
  selectedLicenseTypeId: null,
  isLoading: false,
  licenseTypeCreateLoading: false,
};

const licenseTypesDictionary: StateDictionary<LicenseType> = {
  entityList: 'licenseTypes',
  listLoading: 'isLoading',
  createLoading: 'licenseTypeCreateLoading',
  selectedEntity: '',
  selectedEntityLoading: '',
  entityId: 'id',
  sortFn: (a: LicenseType, b: LicenseType) => a.name.localeCompare(b.name),
};

export const LicenseTypesStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('license-types-store'),
  withComputed(store => ({
    selectedLicenseType: computed(() => store.licenseTypes().find(licenseType => licenseType.id === store.selectedLicenseTypeId()) || null),
  })),
  withMethods((store, licenseTypesService = inject(LicenseTypesService), router = inject(Router)) => {
    createCustomStateSetOnUrlHandler<LicenseTypesState>('/csp/license-types', { selectedLicenseTypeId: null })(store);

    const repositoryMethods = {
      getAll: getAll<LicenseType>({
        store,
        service: licenseTypesService,
        dictionary: licenseTypesDictionary,
      }),

      addLicenseType: addEntity<LicenseType, CreateLicenseTypeRequest>({
        store,
        service: licenseTypesService,
        dictionary: licenseTypesDictionary,
        onSuccessCallback: (newLicenseType: LicenseType) => {
          patchState(store, { selectedLicenseTypeId: newLicenseType.id });
          router.navigate(['csp', 'license-types', newLicenseType.id, 'details']);
        },
      }),

      updateLicenseType: updateEntity<LicenseType, UpdateLicenseTypeRequest>({
        store,
        service: licenseTypesService,
        dictionary: licenseTypesDictionary,
        onSuccessCallback: (updatedLicenseType: LicenseType) => {
          patchState(store, { selectedLicenseTypeId: updatedLicenseType.id });
        },
      }),

      deleteLicenseType: deleteEntity<LicenseType>({
        store,
        service: licenseTypesService,
        dictionary: licenseTypesDictionary,
        onSuccessCallback: () => {
          patchState(store, {
            selectedLicenseTypeId: null,
          });
          router.navigate(['csp', 'license-types']);
        },
      }),
    };

    const navigationActions = {
      selectLicenseType: (id: string): void => {
        if (!store.licenseTypes().length && !store.isLoading()) repositoryMethods.getAll();

        patchState(store, { selectedLicenseTypeId: id });
      },

      goToLicenseTypeActions: async (id: string): Promise<boolean> => {
        const result = await router.navigate(['csp', 'license-types', id]);
        if (!result) return false;
        patchState(store, { selectedLicenseTypeId: id });
        return true;
      },

      goToListPage: (): void => {
        patchState(store, {
          selectedLicenseTypeId: null,
        });
        router.navigate(['csp', 'license-types']);
      },
    };

    return {
      ...repositoryMethods,
      ...navigationActions,
    };
  }),
);

export interface LicenseTypesStoreType {
  readonly licenseTypes: Signal<LicenseType[]>;
  readonly selectedLicenseTypeId: Signal<string | null>;
  readonly isLoading: Signal<boolean>;
  readonly licenseTypeCreateLoading: Signal<boolean>;

  readonly selectedLicenseType: Signal<LicenseType | null>;

  readonly getAll: () => void;
  readonly addLicenseType: (request: CreateLicenseTypeRequest) => void;
  readonly updateLicenseType: (input: { id: string; dto: UpdateLicenseTypeRequest }) => void;
  readonly deleteLicenseType: (id: string) => void;
  readonly selectLicenseType: (id: string) => void;
  readonly goToLicenseTypeActions: (id: string) => Promise<boolean>;
  readonly goToListPage: () => void;
}
